'use client';

import React, { startTransition } from 'react';
import { useEventListener, useScrollShadow, useWindowSize, useClickOutside } from '@benzinga/hooks';
import { Icon } from '../Icon';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faEllipsisH } from '@fortawesome/pro-light-svg-icons/faEllipsisH';
import SelectMenu from '../SelectMenu';
import classnames from 'classnames';
import LazyLoad from 'react-lazyload';
import styles from './Tabs.module.scss';

const LazyLoadComponent = LazyLoad as any;

type TabVariant = 'default' | 'bordered' | 'lifted' | 'pill' | 'full';

export interface TabsInterface {
  component?: React.ReactElement;
  key: string;
  link?: string;
  externalLink?: string;
  icon?: IconDefinition;
  name: string;
}

export interface TabsProps {
  tabs: TabsInterface[];
  onChange?: (tabKey: string) => void;
  activeTab: string;
  enableLazyLoad?: boolean;
  lazyLoadOptions?: {
    height: 400;
    offset: 200;
  };
  variant?: TabVariant;
  isShowMoreButtonEnabled?: boolean;
  tabListWrapperClassname?: string;
  tabBarGutter?: number;
  showScrollShadow?: boolean;
  leftSideElement?: React.ReactNode;
  rightSideElement?: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({
  activeTab,
  enableLazyLoad = false,
  isShowMoreButtonEnabled = true,
  lazyLoadOptions = {
    height: 400,
    offset: 200,
  },
  leftSideElement = null,
  onChange,
  rightSideElement = null,
  showScrollShadow = true,
  tabBarGutter,
  tabListWrapperClassname,
  tabs,
  variant = 'default',
}) => {
  const tabListRef = React.useRef<HTMLDivElement>(null);
  const selectMenuRef = React.useRef<HTMLDivElement>(null);
  const moreItemsButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [visibleTabIndices, setVisibleTabIndices] = React.useState<number[]>([]);
  const [isMounted, setIsMounted] = React.useState(false);
  const [isCalculating, setIsCalculating] = React.useState(false);
  const shouldCalculate = variant !== 'full' && isShowMoreButtonEnabled;
  const { width } = useWindowSize();
  const mobileWidth = width < 760;

  const { showEnd, showStart } = useScrollShadow(tabListRef);
  const shouldShowScrollShadow = !isShowMoreButtonEnabled && showScrollShadow;

  const shadowClasses = React.useMemo(
    () =>
      classnames({
        [styles.overflowShadowBoth]: shouldShowScrollShadow && showStart && showEnd,
        [styles.overflowShadowLeft]: shouldShowScrollShadow && showStart && !showEnd,
        [styles.overflowShadowRight]: shouldShowScrollShadow && !showStart && showEnd,
      }),
    [shouldShowScrollShadow, showEnd, showStart],
  );

  React.useEffect(() => {
    startTransition(() => {
      setIsMounted(true);
      shouldCalculate && setIsCalculating(true);
    });
  }, [shouldCalculate]);

  React.useEffect(() => {
    if (!isShowMoreButtonEnabled) return;
    const tabList = tabListRef?.current;
    if (tabList && isCalculating) {
      tabList.style.overflowX = 'auto';
      const tabElements = Array.from(tabList.children) as HTMLElement[];
      let stopWidth = 36;
      const visible: number[] = [];
      tabElements.forEach((tab, index) => {
        if (moreItemsButtonRef && moreItemsButtonRef.current && visible.length === tabElements.length - 1) {
          stopWidth -= moreItemsButtonRef.current.offsetWidth;
        }

        stopWidth += tab.offsetWidth;
        if (tabList && tabList.offsetWidth >= stopWidth) {
          visible.push(index);
        }
      });

      setVisibleTabIndices(visible);
      tabList.style.overflowX = 'unset';
      setIsCalculating(false);
    }
  }, [tabs, isShowMoreButtonEnabled, isCalculating, shouldCalculate]);

  useEventListener('resize', () => {
    if (!shouldCalculate) return;
    if (mobileWidth) return;
    if (!isCalculating) {
      startTransition(() => {
        setIsCalculating(true);
      });
    }
    startTransition(() => {
      setIsDropdownOpen(false);
    });
  });

  useClickOutside(selectMenuRef, (event?: MouseEvent) => {
    if (!event) {
      setIsDropdownOpen(false);
      return;
    }
    if (
      shouldCalculate &&
      moreItemsButtonRef.current &&
      event.target &&
      !moreItemsButtonRef.current?.contains(event.target as Node)
    ) {
      setIsDropdownOpen(false);
    }
  });

  const handleChangeTab = React.useCallback(
    (newTabKey: string) => {
      if (typeof onChange === 'function') onChange(newTabKey);
    },
    [onChange],
  );

  const handleToggleMoreItems = () => setIsDropdownOpen(!isDropdownOpen);

  let visibleTabs: TabsInterface[] = [];
  const overflowTabs: TabsInterface[] = [];
  if (!isMounted || isCalculating || !shouldCalculate) {
    visibleTabs = tabs;
  } else {
    tabs.forEach((tab, index) => {
      if (visibleTabIndices.includes(index)) {
        visibleTabs.push(tab);
      } else {
        overflowTabs.push(tab);
      }
    });
  }

  const isDropdownTabShown = !isCalculating && isDropdownOpen && overflowTabs.length > 0;

  return (
    <div
      className={classnames(styles.tabsContainer, 'tabs-container', {
        [styles[`${variant}Variant`]]: variant,
        [`${variant}-variant`]: variant,
        [styles.showMoreEnabled]: isShowMoreButtonEnabled,
      })}
      style={
        {
          '--tab-bar-gutter': tabBarGutter ? `${tabBarGutter}px` : '0',
        } as React.CSSProperties
      }
    >
      <div
        className={classnames(styles.tabListWrapper, 'tab-list-wrapper', {
          [`${tabListWrapperClassname}`]: tabListWrapperClassname,
        })}
      >
        {leftSideElement}
        <div className={classnames(styles.tabList, 'tab-list', shadowClasses)} ref={tabListRef}>
          {Array.isArray(visibleTabs) &&
            visibleTabs.map(tab => (
              <TabButton
                activeTab={activeTab}
                key={tab.key}
                onClick={tab.link || tab.externalLink ? undefined : () => handleChangeTab(tab.key)}
                tab={tab}
              />
            ))}
          {isShowMoreButtonEnabled && shouldCalculate && overflowTabs?.length > 0 && (
            <button
              aria-label="Click to see more tab items"
              className={classnames(styles.moreItemsButton, 'more-items-button')}
              onClick={handleToggleMoreItems}
              ref={moreItemsButtonRef}
            >
              <Icon className={classnames(styles.ellipsisIcon, 'ellipsis-icon')} icon={faEllipsisH} />
            </button>
          )}
          {isShowMoreButtonEnabled && shouldCalculate && (
            <div className={classnames(styles.secondaryTabsDropdown, 'secondary-tabs-dropdown')} ref={selectMenuRef}>
              <SelectMenu
                onChange={handleChangeTab}
                open={isDropdownTabShown}
                optionElement={option => {
                  if (option.externalLink) {
                    return (
                      <a
                        className={classnames(styles.tab, 'tab')}
                        data-tab-link-external={option.key}
                        href={option.externalLink}
                        key={option.key}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {option.name}
                      </a>
                    );
                  }
                  if (option.link) {
                    return (
                      <a href={option.a} key={option.key}>
                        {option.name}
                      </a>
                    );
                  }
                  return option.name;
                }}
                options={overflowTabs}
                position="right"
                selected={activeTab}
              />
            </div>
          )}
        </div>
        {rightSideElement}
      </div>
      <div className={classnames(styles.tabsContent, 'tabs-content')}>
        {Array.isArray(tabs) &&
          tabs.map(({ component, key }) => {
            if (activeTab === key || !enableLazyLoad) {
              return (
                <div
                  className={classnames(styles.tabContent, 'tab-content', {
                    [styles.selected]: activeTab === key,
                    selected: activeTab === key,
                  })}
                  key={`tab-content-${key}`}
                >
                  {component}
                </div>
              );
            }

            return (
              <div
                className={classnames(styles.tabContent, 'tab-content', {
                  [styles.selected]: activeTab === key,
                  selected: activeTab === key,
                })}
                key={`tab-content-${key}`}
              >
                <LazyLoadComponent height={lazyLoadOptions?.height || 400} offset={lazyLoadOptions?.offset || 200} once>
                  <>{component}</>
                </LazyLoadComponent>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const TabButton: React.FC<{ activeTab: string; tab: TabsInterface; onClick?: () => void }> = ({
  activeTab,
  onClick,
  tab,
}) => {
  const content = (
    <span>
      {tab.icon && <Icon className="mr-1.5" icon={tab.icon} />}
      {tab.name}
    </span>
  );

  const tabClassName = classnames(styles.tab, 'tab', {
    [styles.active]: activeTab === tab.key,
    active: activeTab === tab.key,
    [styles.promoOffer]: tab.key === 'offer',
    'promo-offer': tab.key === 'offer',
  });

  if (tab.externalLink) {
    return (
      <a
        className={tabClassName}
        data-tab-link-external={tab.key}
        href={tab.externalLink}
        key={`tab-link-${tab.key}`}
        rel="noreferrer"
        target="_blank"
      >
        {content}
      </a>
    );
  }

  if (tab.link) {
    return (
      <a className={tabClassName} href={tab.link} key={`tab-link-${tab.key}`}>
        {content}
      </a>
    );
  }

  return (
    <button className={tabClassName} key={`tab-button-${tab.key}`} name={tab.name} onClick={onClick}>
      {content}
    </button>
  );
};

export default Tabs;
